import { ValueTypes } from '../../common/ValueTypes';
import { ActualDataType, AggregationMode, Dictionary, DigitalOrStringConvertibleValue, DigitalSetSimpleDictionary, IValueViewModel, TagDateValue, WidgetValues } from '../../models';

export interface TagDateDigitalValue extends TagDateValue {
    isDigital?: boolean;
}

export interface IDigitalSetHelperService {
    getValueOrDigital(vm: IValueViewModel, value: DigitalOrStringConvertibleValue, digitalSets: DigitalSetSimpleDictionary, aggregationMode: AggregationMode): number | string;
    isConvertibleToDigitalValue(valueType: string): boolean;
    mapDigitalSetsValues(widgetValues: WidgetValues[], digitalSets: DigitalSetSimpleDictionary, viewModels: Dictionary<IValueViewModel>, aggregationMode: AggregationMode): WidgetValues[];
    mapDigitalSetsForTagDatesValues(values: TagDateValue[], digitalSets: DigitalSetSimpleDictionary, viewModels: Dictionary<IValueViewModel>, aggregationMode: AggregationMode): TagDateValue[];
}

export class DigitalSetHelperService implements IDigitalSetHelperService {
    static serviceId = 'digitalSetHelperService';

    static $inject = [];

    /**
     * Make sure this is the method you need, and check/compare with valueFormattingHelper
     * @param vm 
     * @param value 
     * @param digitalSets 
     * @param aggregationMode 
     * @returns 
     */
    getValueOrDigital(
        vm: IValueViewModel,
        value: DigitalOrStringConvertibleValue,
        digitalSets: DigitalSetSimpleDictionary,
        aggregationMode: AggregationMode
    ): number | string {
        let result: string;

        if (vm) {
            result = this.getValue(value, digitalSets, vm.actualDataType, vm.digitalSetGuid, aggregationMode);
        }

        return result ?? value.value;
    }

    isConvertibleToDigitalValue(valueType: string): boolean {
        if (!valueType) {
            return false;
        }
        const vt = valueType.toLowerCase();
        return (ValueTypes.ValueTypesSupportedByDigitalDataType.indexOf(vt) >= 0);
    }

    mapDigitalSetsValues(widgetValues: WidgetValues[], digitalSets: DigitalSetSimpleDictionary, viewModels: Dictionary<IValueViewModel>, aggregationMode: AggregationMode): WidgetValues[] {
        if (widgetValues?.length > 0 && digitalSets && Object.keys(digitalSets).length > 0 && viewModels && Object.keys(viewModels).length > 0) {
            widgetValues.forEach(wv => {
                wv.aggregatedValue = this.mapDigitalSetsForTagDatesValues(wv.aggregatedValue, digitalSets, viewModels, aggregationMode);
                wv.aggregatedValues = this.mapDigitalSetsForTagDatesValues(wv.aggregatedValues, digitalSets, viewModels, aggregationMode);
                wv.values = this.mapDigitalSetsForTagDatesValues(wv.values, digitalSets, viewModels, aggregationMode);
            });
        }
        return widgetValues;
    }

    mapDigitalSetsForTagDatesValues(
        values: TagDateValue[],
        digitalSets: DigitalSetSimpleDictionary,
        viewModels: Dictionary<IValueViewModel>,
        aggregationMode: AggregationMode
    ): TagDateValue[] {
        if (values && values.length > 0) {
            values
                .filter(v => this.isConvertibleToDigitalValue(v.valueType))
                .forEach(v => v.comment = this.getValueFromViewModel(v, digitalSets, viewModels, aggregationMode));
        }
        return values;
    }

    private getValueFromViewModel(
        value: TagDateValue,
        digitalSets: DigitalSetSimpleDictionary,
        viewModels: Dictionary<IValueViewModel>,
        aggregationMode: AggregationMode
    ): string {
        let result = null;

        const foundVm = viewModels[value.guid];
        if (foundVm) {
            result = this.getValue(value, digitalSets, foundVm.actualDataType, foundVm.digitalSetGuid, aggregationMode);
        }

        return result ?? value.comment;
    }

    private getValue(
        value: DigitalOrStringConvertibleValue,
        digitalSets: DigitalSetSimpleDictionary,
        actualDataType: ActualDataType,
        digitalSetGuid: string,
        aggregationMode: AggregationMode
    ): string {
        if (value.value !== undefined
            && actualDataType
            && actualDataType === ActualDataType.Digital
            && digitalSets
            && digitalSetGuid
            && digitalSets[digitalSetGuid]
            && digitalSets[digitalSetGuid][value.value]
            && this.isConvertibleToDigitalValue(value.valueType)
        ) {
            let result = digitalSets[digitalSetGuid][value.value];
            if (aggregationMode === AggregationMode.Average && value.count !== undefined) {
                const percentage = Math.round(value.count * 100);
                result = value.count === 1
                    ? `${result}`
                    : `${result} (${percentage}%)`;
            }
            return result;
        }
    }
}